import { Link, useNavigate } from '@tanstack/react-router'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { apiGet } from './api.ts'
import { selectCampaign, type Campaign } from './campaign.tsx'

/**
 * Shared by the platform admin screens (docs/decisions.md 2026-10-01): the
 * campaign and agreement lists, the activity behind Today (0094), and the few
 * pieces every one of those screens draws — the live dot, the two-week
 * sparkline, the campaigns table.
 */

export interface AdminCampaign {
  campaign_id: string
  org_id: string
  org_name: string
  name: string
  election_date: string | null
  status: string
  attestation_signed_at: string | null
  attestation_document: string | null
  s19012_notified_at: string | null
  may_cut: boolean
  may_report: boolean
  cut_until: string | null
  report_until: string | null
  dialer_seats: number
  list_type_codes: string[] | null
  purchases: number
  people: number
}

export interface Agreement {
  agreement_id: string
  org_id: string
  name: string
  package: string | null
  basis: string
  price_cents: string | null
  payment_source: string
  payment_note: string | null
  access_from: string | null
  ends_on: string | null
  list_type_codes: string[] | null
  list_credits: number | null
  dialer_seats: number
  walk_licenses: number | null
  note: string | null
  status: string
  void_reason: string | null
  created_at: string
  phones_in_use: number
  walk_today: number
  lists_used: number
  campaigns: { campaign_id: string; name: string; access_from: string; cut_until: string; report_until: string }[]
}

export interface Activity {
  campaign_id: string
  calling_now: number
  walking_now: number
  walk_today: number
  phone_today: number
  trend: number[]
  last_active: string | null
}

export interface FeedItem {
  occurred_at: string
  event_type: string
  actor_email: string | null
  campaign_id: string | null
  campaign_name: string | null
  org_name: string | null
  detail: Record<string, unknown>
}

export function useAdminCampaigns(orgId?: string) {
  return useQuery({
    queryKey: ['admin-campaigns', orgId ?? 'all'],
    queryFn: () =>
      apiGet<{ campaigns: AdminCampaign[] }>(orgId ? `/admin/campaigns?org_id=${orgId}` : '/admin/campaigns'),
  })
}

/** Every agreement, or one customer's, or the ones covering one campaign. */
export function useAgreements(filter: { orgId?: string; campaignId?: string } = {}) {
  const q = filter.orgId ? `?org_id=${filter.orgId}` : filter.campaignId ? `?campaign_id=${filter.campaignId}` : ''
  return useQuery({
    queryKey: ['admin-agreements', q],
    queryFn: () => apiGet<{ agreements: Agreement[] }>(`/admin/agreements${q}`),
  })
}

/**
 * Activity and the feed. Refreshed every minute while a Today-style screen is
 * open: "right now" that is an hour old is not right now.
 */
export function useToday() {
  return useQuery({
    queryKey: ['admin-today'],
    queryFn: () => apiGet<{ activity: Activity[]; feed: FeedItem[] }>('/admin/today', { background: true }),
    refetchInterval: 60_000,
  })
}

export function activityById(activity: Activity[] | undefined): Map<string, Activity> {
  return new Map((activity ?? []).map((a) => [a.campaign_id, a]))
}

/**
 * Go into a campaign's workspace. The campaign list has to know about it
 * first — the header's selection falls back to the first campaign it has if
 * asked for one it has not heard of — so refresh, then select, then go.
 */
export function useOpenWorkspace() {
  const client = useQueryClient()
  const navigate = useNavigate()
  return async (campaignId: string, to = '/overview') => {
    await client.refetchQueries({ queryKey: ['campaigns'] })
    const known = client.getQueryData<{ campaigns: Campaign[] }>(['campaigns'])
    if (!known?.campaigns.some((c) => c.campaign_id === campaignId)) return false
    selectCampaign(campaignId)
    await navigate({ to })
    return true
  }
}

export function money(cents: string | number | null): string | null {
  if (cents === null) return null
  return (Number(cents) / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })
}

/** Days from today to an ISO date, by calendar day. */
export function daysUntil(isoDate: string): number {
  const [y, m, d] = isoDate.split('-').map(Number)
  const then = Date.UTC(y!, m! - 1, d!)
  const now = new Date()
  const today = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate())
  return Math.round((then - today) / 86_400_000)
}

export function shortDate(isoDate: string | null): string {
  if (!isoDate) return '—'
  const [y, m, d] = isoDate.split('-').map(Number)
  return new Date(y!, m! - 1, d!).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

export function ago(iso: string | null): string {
  if (!iso) return 'never'
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60_000)
  if (mins < 2) return 'just now'
  if (mins < 60) return `${mins} min ago`
  const hours = Math.round(mins / 60)
  if (hours < 24) return `${hours} hr ago`
  const days = Math.round(hours / 24)
  return days === 1 ? 'yesterday' : `${days} days ago`
}

/** Green-blue pulse for "people are working right now"; grey when quiet. */
export function Live({ on, children }: { on: boolean; children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
      <span
        aria-hidden
        className={on ? 'h-2 w-2 rounded-full bg-brand-500 ring-3 ring-brand-100' : 'h-2 w-2 rounded-full bg-line'}
      />
      {children}
    </span>
  )
}

/**
 * Two weeks of results as a sparkline: the shape, not the numbers. One scale
 * per line (each campaign against its own best day), which is what "is this
 * campaign picking up or tailing off" needs.
 */
export function Spark({ values, label }: { values: number[]; label: string }) {
  const w = 120
  const h = 28
  const max = Math.max(...values, 1)
  const step = values.length > 1 ? w / (values.length - 1) : w
  const pts = values.map((v, i) => [i * step, h - 3 - (v / max) * (h - 6)] as const)
  const line = pts.map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join(' ')
  const last = pts[pts.length - 1] ?? [w, h - 3]
  return (
    <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" className="block h-7 w-full" role="img" aria-label={label}>
      <polygon points={`0,${h} ${line} ${w},${h}`} className="fill-brand-100" />
      <polyline points={line} fill="none" className="stroke-brand-600" strokeWidth={1.5} vectorEffect="non-scaling-stroke" />
      <circle cx={last[0]} cy={last[1]} r={2.5} className="fill-brand-600" />
    </svg>
  )
}

export type Tone = 'open' | 'report' | 'closed' | 'attention'

export function statusOf(c: AdminCampaign): { text: string; tone: Tone } {
  if (c.status !== 'active') return { text: c.status, tone: 'closed' }
  if (!c.attestation_signed_at) return { text: 'Needs its attestation', tone: 'attention' }
  if (c.purchases === 0 && !c.may_report) return { text: 'Nothing purchased', tone: 'attention' }
  if (c.may_cut) return { text: 'Open', tone: 'open' }
  if (c.may_report) return { text: `Reporting only until ${shortDate(c.report_until)}`, tone: 'report' }
  return { text: 'Closed · data kept', tone: 'closed' }
}

export function Status({ c }: { c: AdminCampaign }) {
  const s = statusOf(c)
  // Gold is the warning convention and is used only for "needs attention".
  const cls =
    s.tone === 'open'
      ? 'bg-brand-100 text-brand-800'
      : s.tone === 'attention'
        ? 'bg-gold-300/40 text-ink'
        : 'bg-surface-sunken text-ink-muted'
  return <span className={`inline-block whitespace-nowrap rounded px-2 py-0.5 text-xs font-medium ${cls}`}>{s.text}</span>
}

function RightNow({ c, a }: { c: AdminCampaign; a: Activity | undefined }) {
  if (statusOf(c).tone !== 'open') return <Status c={c} />
  const calling = a?.calling_now ?? 0
  const walking = a?.walking_now ?? 0
  if (!calling && !walking) return <Live on={false}>Quiet</Live>
  return (
    <Live on>
      {[calling ? `${calling} calling` : '', walking ? `${walking} walking` : ''].filter(Boolean).join(' · ')}
    </Live>
  )
}

const TH = 'py-2 pr-4 font-semibold'

/**
 * The campaigns table every platform screen uses: who it is, when its election
 * is, what it is doing right now, today, and the last two weeks.
 */
export function CampaignTable({
  campaigns,
  activity,
  showCustomer,
}: {
  campaigns: AdminCampaign[]
  activity: Map<string, Activity>
  showCustomer: boolean
}) {
  return (
    <div className="overflow-x-auto rounded-lg border border-line">
      <table className="w-full min-w-[46rem] border-collapse text-sm">
        <thead className="bg-surface-sunken">
          <tr className="text-left text-xs tracking-wide text-ink-muted uppercase">
            <th scope="col" className={`${TH} pl-3`}>Campaign</th>
            {showCustomer ? <th scope="col" className={TH}>Customer</th> : null}
            <th scope="col" className={TH}>Election</th>
            <th scope="col" className={TH}>Right now</th>
            <th scope="col" className={`${TH} text-right`}>Today</th>
            <th scope="col" className={`${TH} w-36`}>Last 14 days</th>
          </tr>
        </thead>
        <tbody>
          {campaigns.length === 0 ? (
            <tr><td colSpan={6} className="px-3 py-6 text-ink-muted">No campaigns yet.</td></tr>
          ) : (
            campaigns.map((c) => {
              const a = activity.get(c.campaign_id)
              const days = c.election_date ? daysUntil(c.election_date) : null
              return (
                <tr key={c.campaign_id} className="border-t border-line align-middle hover:bg-surface-sunken/60">
                  <td className="py-2 pr-4 pl-3">
                    <Link
                      to="/platform/campaigns/$campaignId"
                      params={{ campaignId: c.campaign_id }}
                      className="font-semibold text-brand-700 hover:underline"
                    >
                      {c.name}
                    </Link>
                  </td>
                  {showCustomer ? (
                    <td className="py-2 pr-4">
                      <Link to="/platform/customers/$orgId" params={{ orgId: c.org_id }} className="hover:underline">
                        {c.org_name}
                      </Link>
                    </td>
                  ) : null}
                  <td className="py-2 pr-4 whitespace-nowrap tabular-nums">
                    {shortDate(c.election_date)}
                    {days !== null ? (
                      <div className="text-xs text-ink-muted">{days >= 0 ? `${days} days` : 'past'}</div>
                    ) : null}
                  </td>
                  <td className="py-2 pr-4"><RightNow c={c} a={a} /></td>
                  <td className="py-2 pr-4 text-right tabular-nums">
                    {((a?.walk_today ?? 0) + (a?.phone_today ?? 0)).toLocaleString()}
                  </td>
                  <td className="py-2 pr-4">
                    <Spark values={a?.trend ?? []} label={`${c.name}: results per day, last 14 days`} />
                    <div className="text-xs text-ink-muted">active {ago(a?.last_active ?? null)}</div>
                  </td>
                </tr>
              )
            })
          )}
        </tbody>
      </table>
    </div>
  )
}
